import type { ReactNode } from "react";
import { formatUsd, type Quote } from "../../../convex/lib/quote";

const DOT: Record<string, string> = {
  messages: "var(--ink-dim)",
  pics: "var(--slack)",
  video: "var(--discord)",
  files: "var(--live)",
};

/**
 * The estimator's signature element: a live, itemized migration receipt that
 * re-tallies as the customer toggles content, channels, and time range. The
 * total is the one champagne-lit number on the page.
 */
export function QuoteReceipt({
  quote,
  truncated = false,
  children,
}: {
  quote: Quote;
  truncated?: boolean;
  children?: ReactNode;
}) {
  return (
    <aside className="receipt card" aria-live="polite">
      <div className="receipt-head">
        <span className="eyebrow">Your quote</span>
        <span className="mono receipt-once">one-time</span>
      </div>

      <div className="receipt-lines">
        {quote.lineItems.length === 0 && (
          <p className="muted mono receipt-empty">
            Toggle on what you want to migrate.
          </p>
        )}
        {quote.lineItems.map((li) => (
          <div className="receipt-line" key={li.key}>
            <span className="receipt-dot" style={{ background: DOT[li.key] }} />
            <span className="receipt-label">{li.label}</span>
            <span className="receipt-detail mono">{li.detail}</span>
            <span className="receipt-amt mono">{formatUsd(li.amountCents)}</span>
          </div>
        ))}
      </div>

      {quote.floorApplied && !quote.isFree && (
        <p className="receipt-note mono">$100 job minimum applied.</p>
      )}
      {truncated && (
        <p className="receipt-note mono">
          Large workspace — totals are a conservative floor.
        </p>
      )}

      <div className="receipt-total">
        <span>{quote.isFree ? "Free proof-of-concept" : "Total"}</span>
        <span className="receipt-total-amt gradient-text">
          {formatUsd(quote.amountCents)}
        </span>
      </div>

      {children && <div className="receipt-cta">{children}</div>}
    </aside>
  );
}
